import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { encStorage } from "../hooks/encStorage";
import { GET_MARKETDATA } from "../services/Apis";
import Header from "../sections/Header";
import Sidebar from "../sections/Sidebar";
import usePrevious from '../hooks/usePrevious'; // Import the usePrevious hook
import moment from 'moment';

const MarketDataTable = () => {
    const token = encStorage.getItem("token");
    const [marketData, setMarketData] = useState([]);
    const [sidebarState, setSidebarState] = useState(false);
    const [activeInstrumentRow, setactiveInstrumentRow] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const updateSidebarState = (newState) => {
        setSidebarState(newState);
    };

    const fetchMarketData = async () => {
        const response = await fetch(GET_MARKETDATA, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const jsonData = await response.json();
        setMarketData(jsonData.data);
        return jsonData;
    };

    const { error, isLoading } = useQuery({
        queryKey: ['marketData'],
        queryFn: fetchMarketData,
        refetchInterval: 500,
        staleTime: 500,
    });

    const previousData = usePrevious(marketData);

    const getCellClass = (currentValue, previousValue) => {
        if (previousValue === undefined) return '';
        return currentValue > previousValue ? 'bg-trade-up' : currentValue < previousValue ? 'bg-trade-down' : '';
    };

    const getSymbolClass = (currentValue, previousValue) => {
        if (previousValue === undefined) return '';
        return currentValue > previousValue ? 'fa-sort-asc text-trade-up' : currentValue < previousValue ? 'fa-sort-desc text-trade-down' : '';
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error fetching data: {error.message}</div>;

    return (
        <>
            <Header updateState={updateSidebarState} />
            <div id="layoutSidenav">
                <Sidebar props={sidebarState} />
                <div id="layoutSidenav_content" className="layout-content">
                    <main>
                        <div className="container-fluid px-4">
                            <div className="card mb-4">
                                <div className="card-title ps-3 pt-3 text-bold">
                                    <h3 className="mt-4 w-75">Market Watch</h3>
                                </div>
                                <div className={`card-body ${isFullscreen ? 'fullscreen-table-container' : ''}`}>

                                    <table className="fullscreen-table bg-market-table" border="0" cellSpacing={2} cellPadding={2}>
                                        <thead className='bg-market-thead'>
                                            <tr className='fs-6'>
                                                <th className="text-end" style={{ "width": "2%" }}></th>
                                                <th style={{ "width": "4%" }}>Exch</th>
                                                <th style={{ "width": "11%" }}>Name</th>
                                                <th style={{ "width": "9%" }}>Expiry</th>
                                                <th className="text-end" style={{ "width": "5%" }}>Buy Qty</th>
                                                <th className="text-end" style={{ "width": "6%" }}>Buy Price</th>
                                                <th className="text-end" style={{ "width": "6%" }}>Sell Price</th>
                                                <th className="text-end" style={{ "width": "7%" }}>% Change</th>
                                                <th className="text-end" style={{ "width": "6%" }}>LTP</th>
                                                <th style={{ "width": "6%" }}>LTT</th>
                                                <th className="text-end" style={{ "width": "5%" }}>Open</th>
                                                <th className="text-end" style={{ "width": "5%" }}>High</th>
                                                <th className="text-end" style={{ "width": "5%" }}>Close</th>
                                                <th className="text-end" style={{ "width": "5%" }}>Low</th>
                                                <th className="text-end" style={{ "width": "9%" }}>Lower Circuit</th>
                                                <th className="text-end" style={{ "width": "9%" }}>Upper Circuit</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {marketData.length > 0 ? (
                                                marketData.map((item, index) => {
                                                    const prevItem = previousData ? previousData[index] : {};
                                                    return (
                                                        <tr
                                                            key={item.instrument_token}
                                                            className={`market-row ${activeInstrumentRow == item.instrument_token ? 'market-active-row' : ''}`}
                                                            onClick={() => {
                                                                setactiveInstrumentRow(item.instrument_token);
                                                            }}
                                                        >
                                                            <td className={`d-flex align-items-center justify-centent-center`}>
                                                                <i className={`fa ${getSymbolClass(item.last_price, prevItem?.last_price)} ms-1 fs-6`} aria-hidden="true"></i>
                                                            </td>
                                                            <td>{item.exchange}</td>
                                                            <td>{item.name}</td>
                                                            <td>{item.expiry}</td>
                                                            <td className={`text-end`}>{item.buy_quantity}</td>
                                                            <td className={`text-end ${getCellClass(item.buy_price_0, prevItem?.buy_price_0)}`}>
                                                                {item.buy_price_0}
                                                            </td>
                                                            <td className={`text-end ${getCellClass(item.sell_price_0, prevItem?.sell_price_0)}`}>
                                                                {item.sell_price_0}
                                                            </td>
                                                            <td className={`text-end ${getCellClass(item.percentage_change, prevItem?.percentage_change)}`}>
                                                                {item.percentage_change}
                                                            </td>
                                                            <td className={`text-end ${getCellClass(item.last_price, prevItem?.last_price)}`}>
                                                                {item.last_price}
                                                            </td>
                                                            <td>{(() => {
                                                                const time = moment(item.last_trade_time, 'DD-MM-YYYY HH:mm:ss').format('HH:mm:ss');

                                                                return time;
                                                            })()}</td>
                                                            <td className={`text-end ${getCellClass(item.ohlc_open, prevItem?.ohlc_open)}`}>
                                                                {item.ohlc_open}
                                                            </td>
                                                            <td className={`text-end ${getCellClass(item.ohlc_high, prevItem?.ohlc_high)}`}>
                                                                {item.ohlc_high}
                                                            </td>
                                                            <td className={`text-end ${getCellClass(item.ohlc_low, prevItem?.ohlc_low)}`}>
                                                                {item.ohlc_low}
                                                            </td>
                                                            <td className={`text-end ${getCellClass(item.ohlc_close, prevItem?.ohlc_close)}`}>
                                                                {item.ohlc_close}
                                                            </td>
                                                            <td className={`text-end ${getCellClass(item.lower_circuit, prevItem?.lower_circuit)}`}>
                                                                {item.lower_circuit}
                                                            </td>
                                                            <td className={`text-end ${getCellClass(item.upper_circuit, prevItem?.upper_circuit)}`}>
                                                                {item.upper_circuit}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="15">No data available</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </>
    );
};

export default MarketDataTable;